import * as React from 'react'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type { CalendarHoldDto } from '@lib/dto/venue-calendar.dto'
import { useCalendarAroundMonth } from '@/hooks/useBookedDays'

/** What a date picker says a held day means. */
export const HELD_LABEL = 'On hold for another couple'

/**
 * The days around a month that another inquiry holds, where it would meet these
 * tracks: the second mark a date picker shows, beside the booked one. A hold
 * warns and never refuses (docs/funnel-stages.spec.md), so this only marks.
 *
 * Unlike a booking, a hold with no tracks isn't harmless: it is the whole
 * venue's day spoken for. So a hold meets these tracks when either side has
 * none, or they share one.
 *
 * Reads the same calendar as `useBookedDays` (`useCalendarAroundMonth`), so
 * the two marks cover the same days and a picker asks once.
 */
export function useHeldDays({
  venueId,
  trackIds,
  month,
  excludeEventId = null,
}: {
  venueId: string
  trackIds: readonly string[]
  /** Any day in the month the picker is showing, as `yyyy-MM-dd`. */
  month: CalendarDay
  excludeEventId?: string | null
}): CalendarDay[] {
  const enabled = venueId !== ''
  const holds = useCalendarAroundMonth(venueId, month, enabled)?.holds

  return React.useMemo(
    () => (enabled && holds ? heldDays(holds, trackIds, excludeEventId) : []),
    [enabled, holds, trackIds, excludeEventId],
  )
}

/** The distinct days of the holds that meet `trackIds`, other than `excludeEventId`'s own. */
export function heldDays(
  holds: readonly CalendarHoldDto[],
  trackIds: readonly string[],
  excludeEventId: string | null,
): CalendarDay[] {
  const days = new Set<CalendarDay>()
  for (const hold of holds) {
    if (hold.eventId === excludeEventId) continue
    const meets = hold.trackIds.length === 0 || trackIds.length === 0 || hold.trackIds.some((id) => trackIds.includes(id))
    if (meets) days.add(hold.date)
  }
  return [...days].sort()
}
